import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, uploadClubDocument } from "@/lib/api";
import { formatDate } from "@/lib/dates";
import { DateField } from "@/components/DateField";
import {
  IdentificationBadge, UsersThree, AddressBook, CaretRight, CheckCircle, WarningCircle,
  LockSimple, PaperPlaneTilt, CalendarBlank, ClipboardText, DownloadSimple, CircleNotch, UserPlus,
  CalendarPlus, ArrowsClockwise, Trash, Plus, ChartPieSlice,
} from "@phosphor-icons/react";
import { PageHero, Card, C, Loader } from "@/components/district/kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { MeetingsDialog, SponsoredClubsDialog } from "@/components/GovBrief";
import { GivingBandsCard } from "@/pages/district/GivingBandsCard";
import { useT } from "@/i18n";
import { toast } from "sonner";

const VISIT_WORD = {
  draft: "Being planned", scheduled: "Scheduled", rescheduled: "Rescheduled",
  invited: "Scheduled", cancelled: "Cancelled", completed: "Completed", closed: "Completed",
};
const fmtDate = (iso) => formatDate(iso);
const fmtShort = (iso) => formatDate(iso);
const daysTo = (iso) => {
  const d = new Date(iso), now = new Date();
  return Math.round((new Date(d.getFullYear(), d.getMonth(), d.getDate()) -
    new Date(now.getFullYear(), now.getMonth(), now.getDate())) / 86400000);
};
const countdownLabel = (iso) => {
  const n = daysTo(iso);
  if (n > 1) return `${n} days to go`;
  if (n === 1) return "Tomorrow";
  if (n === 0) return "Today";
  if (n === -1) return "Yesterday";
  return `${-n} days ago`;
};
// Board/Committees/Sub-committees are the roster builders (left rail). "Board Meeting
// Attendance" is a document, not a roster, so it must NOT be excluded here.
const isRoster = (name) => /committee/i.test(name || "") || (/board/i.test(name || "") && !/meeting/i.test(name || ""));
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// GOV date + Mock GOV, shown at the top-right of the hero.
function HeroVisit({ clubId, visit, mock, reload, canParticipate = true }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  const confirmed = mock?.status === "confirmed";
  const propose = async () => {
    if (!date) { toast.error("Pick a mock-GOV date"); return; }
    setBusy(true);
    try { await api.post(`/clubs/${clubId}/mock-gov`, { date }); toast.success("Mock-GOV date proposed"); setOpen(false); await reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const confirm = async () => {
    setBusy(true);
    try { await api.post(`/clubs/${clubId}/mock-gov/confirm`); toast.success("Mock-GOV confirmed"); await reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not confirm"); }
    setBusy(false);
  };
  const subscribeCalendar = async () => {
    try {
      const r = await api.get("/me/calendar-feed");
      const url = `${api.defaults.baseURL}${r.data.path}`;
      await navigator.clipboard?.writeText(url);
      toast.success("Calendar link copied — add it as a subscription in your calendar app to auto-sync GOV dates");
    } catch { toast.error("Could not create calendar link"); }
  };
  return (
    <div className="space-y-3" data-testid="hero-visit">
      <div className="rounded-2xl bg-white/12 px-5 py-4 backdrop-blur-sm">
        <p className="flex items-center gap-1.5 text-[13px] font-semibold uppercase tracking-wider text-white/75"><CalendarBlank size={14} weight="bold" /> {t("club_govs_visit")}</p>
        {visit?.scheduled_start_utc
          ? <p className="mt-1 text-[22px] font-bold leading-tight" data-testid="hero-gov-date">{fmtDate(visit.scheduled_start_utc)}</p>
          : <p className="mt-1 text-[16px] font-semibold text-white/85">{t("club_not_scheduled")}</p>}
        {visit?.scheduled_start_utc && daysTo(visit.scheduled_start_utc) >= 0 && (
          <span data-testid="hero-countdown" className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-[13px] font-bold" style={{ color: C.indigoText }}>
            <CalendarBlank size={13} weight="bold" /> {countdownLabel(visit.scheduled_start_utc)}
          </span>
        )}
        {visit && <p className="mt-1 text-[13px] font-medium text-white/70">{VISIT_WORD[visit.status] || visit.status}</p>}
        {visit?.scheduled_start_utc && (
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px] font-semibold">
            <a data-testid="hero-add-calendar" href={`${api.defaults.baseURL}/clubs/${clubId}/gov/calendar.ics`} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-white/90 underline underline-offset-2"><CalendarPlus size={13} weight="bold" /> {t("club_add_calendar")}</a>
            <button data-testid="hero-subscribe" onClick={subscribeCalendar}
              className="inline-flex items-center gap-1 text-white/90 underline underline-offset-2"><ArrowsClockwise size={13} weight="bold" /> {t("club_sync")}</button>
          </div>
        )}
      </div>
      <div className="rounded-2xl bg-white/12 px-5 py-4 backdrop-blur-sm">
        <p className="flex items-center gap-1.5 text-[13px] font-semibold uppercase tracking-wider text-white/75"><ClipboardText size={14} weight="bold" /> {t("club_mock_gov")}</p>
        {confirmed ? (
          <p className="mt-1 flex items-center gap-1.5 text-[18px] font-bold leading-tight" data-testid="hero-mock-confirmed"><CheckCircle size={18} weight="fill" /> {fmtShort(mock.date)}</p>
        ) : mock?.date ? (
          <div className="mt-1">
            <p className="text-[16px] font-bold">{fmtShort(mock.date)} · {t("club_proposed")}</p>
            {mock?.can_confirm ? (
              <button data-testid="hero-mock-confirm" onClick={confirm} disabled={busy} className="mt-1 rounded-full bg-white px-3 py-1 text-[13px] font-bold" style={{ color: C.indigoText }}>{t("club_confirm")}</button>
            ) : (
              <p data-testid="hero-mock-awaiting" className="mt-1 text-[12.5px] font-semibold text-white/80">{mock.awaiting_label}</p>
            )}
          </div>
        ) : (
          canParticipate
            ? <button data-testid="hero-mock-schedule" onClick={() => setOpen(true)} className="mt-1.5 rounded-full bg-white px-4 py-1.5 text-[14px] font-bold" style={{ color: C.indigoText }}>{t("club_schedule_now")}</button>
            : <p className="mt-1 text-[13px] font-semibold text-white/75">{t("club_not_scheduled")}</p>
        )}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="mock-schedule-dialog">
          <DialogHeader><DialogTitle>{t("club_schedule_mock_title")}</DialogTitle></DialogHeader>
          <p className="text-[14px]" style={{ color: C.sub }}>{t("club_schedule_mock_desc")}</p>
          <DateField value={date} onChange={setDate} testid="mock-date" placeholder={t("club_pick_date")}
            className="mt-2 flex w-full items-center gap-2 rounded-xl border px-3 py-2.5 text-[15px] outline-none" style={{ borderColor: C.line, color: date ? C.ink : C.sub }} />
          <DialogFooter>
            <button data-testid="mock-propose" onClick={propose} disabled={busy} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[15px] font-bold text-white" style={{ background: C.indigo }}>
              {busy ? <CircleNotch size={15} className="animate-spin" /> : <CalendarBlank size={15} weight="bold" />} {t("club_propose_date")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RoleCard({ icon: Icon, title, sub, onClick, disabled, tint, status, testid }) {
  return (
    <button data-testid={testid} onClick={disabled ? undefined : onClick} disabled={disabled} className={`group w-full text-left ${disabled ? "cursor-not-allowed opacity-70" : ""}`}>
      <Card className="flex items-center gap-4 transition-all duration-200 group-hover:-translate-y-0.5">
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl" style={{ background: tint + "16", color: tint }}>
          {disabled ? <LockSimple size={22} weight="bold" /> : <Icon size={24} weight="bold" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[16px] font-bold tracking-tight" style={{ color: C.ink }}>{title}</span>
          <span className="mt-0.5 block text-[13px] leading-snug" style={{ color: C.sub }}>{sub}</span>
        </span>
        {status === "submitted" && <span className="rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ background: "#E4F7EF", color: "#0F8A5F" }}>Submitted</span>}
        {!disabled && <CaretRight size={16} weight="bold" className="shrink-0" style={{ color: C.sub }} />}
      </Card>
    </button>
  );
}

const DOC_GROUPS = ["Admin", "Financial"];
function docGroup(name) {
  const n = (name || "").toLowerCase();
  if (/\btrf\b|contribution|foundation/.test(n)) return "TRF"; // excluded from centre — covered in Foundation giving
  if (/invoice|payment|dues|budget|audited|account|fee|\bsar\b|trust/.test(n)) return "Financial";
  return "Admin"; // everything else, incl. former Extension (board, committees, sponsored)
}

// Compact "app in a folder" tile for a single document/requirement.
function ItemTile({ clubId, item, docs, hasInfo, canEdit, reload, navigate, onSponsored, onMeetings, onInfo }) {
  const [busy, setBusy] = useState(false);
  const isMeeting = /meeting attendance/i.test(item.name || "");
  const isSponsored = /rotaract|interact|community corps|rotary clubs sponsored/i.test(item.name || "");
  const isInfo = item.kind === "info";
  const done = docs.length > 0 || hasInfo;
  const upload = async (file) => {
    setBusy(true);
    try { await uploadClubDocument(clubId, item.item_id, file); toast.success("Uploaded"); await reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };
  const cls = "flex h-[132px] flex-col items-center justify-center gap-1.5 rounded-2xl border p-2 text-center transition-transform hover:-translate-y-0.5";
  const style = { borderColor: C.line, background: done ? "#F1FBF6" : "#FBFBFE" };
  const Inner = () => (
    <>
      <span className="grid h-9 w-9 place-items-center rounded-xl" style={{ background: done ? "#DDF3E8" : C.indigoSoft, color: done ? "#0F8A5F" : C.indigoText }}>
        {busy ? <CircleNotch size={16} className="animate-spin" /> : done ? <CheckCircle size={18} weight="fill" /> : <ClipboardText size={16} weight="bold" />}
      </span>
      <span className="line-clamp-4 text-[11.5px] font-semibold leading-tight" style={{ color: C.ink }}>{item.name}</span>
    </>
  );
  const testid = `doc-tile-${item.item_id}`;
  if (isMeeting)
    return <button data-testid={testid} onClick={() => onMeetings(/board/i.test(item.name) ? "board" : "club")} className={cls} style={style}><Inner /></button>;
  if (done && docs.length > 0)
    return <a data-testid={testid} href={`${API_BASE}/documents/${docs[0].doc_id}/download`} target="_blank" rel="noreferrer" className={cls} style={style}><Inner /></a>;
  if (isSponsored)
    return <button data-testid={testid} onClick={() => onSponsored(item)} className={cls} style={style}><Inner /></button>;
  if (isInfo)
    return <button data-testid={testid} onClick={() => onInfo(item)} className={cls} style={style}><Inner /></button>;
  if (!canEdit)
    return <div data-testid={testid} className={cls} style={style}><Inner /></div>;
  return (
    <label data-testid={testid} className={`${cls} cursor-pointer`} style={style}>
      <Inner />
      <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
    </label>
  );
}

function DocFolder({ title, items, isDone, shared }) {
  const { t } = useT();
  const done = items.filter(isDone).length;
  const label = title === "Admin" ? t("club_docs_admin") : title === "Financial" ? t("club_docs_financial") : title;
  return (
    <div className="rounded-2xl border p-4" style={{ borderColor: C.line }} data-testid={`doc-folder-${title.toLowerCase()}`}>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-[15px] font-bold tracking-tight" style={{ color: C.ink }}>{label}</h3>
        <span className="text-[13px] font-semibold" style={{ color: C.sub }}>{done}/{items.length}</span>
      </div>
      <div className="grid grid-cols-3 gap-2.5">
        {items.map((it) => <ItemTile key={it.item_id} item={it} {...shared(it)} />)}
      </div>
    </div>
  );
}

function GenderMixCard({ clubId }) {
  const { t } = useT();
  const [g, setG] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get(`/clubs/${clubId}/gender-mix`).then((r) => setG(r.data)).catch(() => {}); }, [clubId]);
  if (!g) return (
    <Card data-testid="gender-mix-card" style={{ border: "1.5px solid #2563EB" }}>
      <p className="text-[15px] font-bold" style={{ color: C.ink }}>{t("club_gender_mix")}</p>
      <div className="flex items-center justify-center py-8"><CircleNotch size={20} className="animate-spin" style={{ color: C.sub }} /></div>
    </Card>
  );
  const set = (grp, key, val) => setG({ ...g, [grp]: { ...g[grp], [key]: Math.max(0, parseInt(val, 10) || 0) } });
  const save = async () => {
    setSaving(true);
    try { await api.put(`/clubs/${clubId}/gender-mix`, { members: g.members, board: g.board, inducted: g.inducted }); toast.success("Gender mix saved"); }
    catch { toast.error("Could not save gender mix"); }
    setSaving(false);
  };
  const Row = ({ label, grp }) => (
    <div className="mt-3">
      <p className="mb-1.5 text-[13px] font-semibold" style={{ color: C.ink }}>{label}</p>
      <div className="grid grid-cols-3 gap-2">
        {[["male", t("club_male")], ["female", t("club_female")], ["other", t("club_other")]].map(([k, lbl]) => (
          <label key={k} className="rounded-lg border px-2.5 py-1.5" style={{ borderColor: C.line }}>
            <span className="block text-[11px] font-medium" style={{ color: C.sub }}>{lbl}</span>
            <input data-testid={`gm-${grp}-${k}`} type="number" min="0" inputMode="numeric" disabled={!g.can_edit}
              value={g[grp][k] ?? 0} onChange={(e) => set(grp, k, e.target.value)}
              className="w-full bg-transparent text-[16px] font-bold outline-none" style={{ color: C.ink }} />
          </label>
        ))}
      </div>
    </div>
  );
  return (
    <Card data-testid="gender-mix-card" style={{ border: "1.5px solid #2563EB" }}>
      <p className="text-[15px] font-bold" style={{ color: C.ink }}>{t("club_gender_mix")}</p>
      <p className="text-[12.5px]" style={{ color: C.sub }}>{t("club_gender_mix_sub")}</p>
      <Row label={t("club_total_membership")} grp="members" />
      <Row label={t("club_board")} grp="board" />
      <Row label={t("club_new_inducted")} grp="inducted" />
      <p className="mt-1 text-[11.5px]" style={{ color: C.sub }}>Count only members inducted this year who have a Member ID (validates the induction into Rotary).</p>
      {g.can_edit && (
        <button data-testid="gm-save" onClick={save} disabled={saving}
          className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full py-2.5 text-[14px] font-semibold text-white disabled:opacity-60" style={{ background: C.indigo }}>
          <CheckCircle size={16} weight="bold" /> {t("save")}
        </button>
      )}
    </Card>
  );
}

const REC_GROUPS = [
  ["Paul Harris Fellow", [["phf", "Paul Harris Fellows"]]],
  ["Major Donors", [["md1", "Level 1"], ["md2", "Level 2"], ["md3", "Level 3"], ["md4", "Level 4"]]],
  ["Bequest Society", [["bq1", "Level 1"], ["bq2", "Level 2"], ["bq3", "Level 3"], ["bq4", "Level 4"]]],
  ["Arch Klumph Society", [["aks_tc", "Trustee Circle"], ["aks_ptc", "Platinum Trustee Circle"]]],
];

function FoundationGivingCard({ clubId }) {
  const { t } = useT();
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get(`/clubs/${clubId}/foundation-giving`).then((r) => setF(r.data)).catch(() => {});
  useEffect(() => { load(); }, [clubId]);
  if (!f) return (
    <Card data-testid="foundation-giving-card" style={{ border: "1.5px solid #E08600" }}>
      <p className="text-[15px] font-bold" style={{ color: C.ink }}>{t("club_foundation_giving")}</p>
      <div className="flex items-center justify-center py-10"><CircleNotch size={22} className="animate-spin" style={{ color: C.sub }} /></div>
    </Card>
  );
  const cats = [["annual_fund", t("club_annual_fund")], ["polio_plus", t("club_polio_plus")], ["other_funds", t("club_other_funds")], ["endowment", t("club_endowment")]];
  const thisYear = cats.reduce((s, [k]) => s + (parseFloat(f[k]) || 0), 0);
  const perCapita = f.member_count ? thisYear / f.member_count : 0;
  const set = (k, v) => setF({ ...f, [k]: v });
  const rec = f.recognitions || {};
  const setRec = (k, field, v) => setF({
    ...f, recognitions: { ...rec, [k]: { ...(rec[k] || { prev: 0, year: 0 }), [field]: Math.max(0, parseInt(v, 10) || 0) } },
  });
  const save = async () => {
    setSaving(true);
    try { await api.put(`/clubs/${clubId}/foundation-giving`, { total_prev: f.total_prev, annual_fund: f.annual_fund, polio_plus: f.polio_plus, other_funds: f.other_funds, endowment: f.endowment, recognitions: rec }); toast.success("Foundation giving saved"); await load(); }
    catch { toast.error("Could not save"); }
    setSaving(false);
  };
  const Money = ({ k, label }) => (
    <label className="block">
      <span className="text-[12px] font-medium" style={{ color: C.sub }}>{label}</span>
      <div className="mt-0.5 flex items-center gap-1 rounded-lg border px-2.5 py-1.5" style={{ borderColor: C.line }}>
        <span style={{ color: C.sub }}>$</span>
        <input data-testid={`fg-${k}`} type="number" min="0" step="0.01" inputMode="decimal" disabled={!f.can_edit}
          value={f[k] ?? 0} onChange={(e) => set(k, e.target.value)} className="w-full bg-transparent text-[15px] font-bold outline-none" style={{ color: C.ink }} />
      </div>
    </label>
  );
  return (
    <Card data-testid="foundation-giving-card" style={{ border: "1.5px solid #E08600" }}>
      <p className="text-[15px] font-bold" style={{ color: C.ink }}>{t("club_foundation_giving")}</p>
      <p className="text-[12.5px]" style={{ color: C.sub }}>{t("club_foundation_sub")}</p>
      <div className="mt-3"><Money k="total_prev" label={t("club_total_prev")} /></div>
      <p className="mb-1 mt-4 text-[13px] font-semibold" style={{ color: C.ink }}>{t("club_this_year")}</p>
      <div className="grid grid-cols-2 gap-2">{cats.map(([k, l]) => <Money key={k} k={k} label={l} />)}</div>
      <div className="mt-3 flex gap-2">
        <div className="flex-1 rounded-lg px-3 py-2" style={{ background: C.indigoSoft }}>
          <p className="text-[10.5px] font-semibold uppercase tracking-wide" style={{ color: C.sub }}>{t("club_total_this_year")}</p>
          <p className="text-[16px] font-bold" style={{ color: C.indigoText }} data-testid="fg-total">${thisYear.toLocaleString()}</p>
        </div>
        <div className="flex-1 rounded-lg px-3 py-2" style={{ background: C.indigoSoft }}>
          <p className="text-[10.5px] font-semibold uppercase tracking-wide" style={{ color: C.sub }}>{t("club_per_capita")}</p>
          <p className="text-[16px] font-bold" style={{ color: C.indigoText }} data-testid="fg-percapita">${perCapita.toFixed(2)}</p>
        </div>
      </div>
      <div className="mt-4 border-t pt-3" style={{ borderColor: C.line }}>
        <p className="text-[13px] font-bold" style={{ color: C.ink }}>{t("club_recognitions")}</p>
        <p className="text-[11.5px]" style={{ color: C.sub }}>Number of recognitions (not amounts).</p>
        <div className="mt-2 grid grid-cols-[1fr_52px_52px] items-center gap-x-2 text-[10px] font-bold uppercase tracking-wide" style={{ color: C.sub }}>
          <span />
          <span className="text-center">Till last yr</span>
          <span className="text-center">This yr</span>
        </div>
        {REC_GROUPS.map(([title, items]) => (
          <div key={title} className="mt-2">
            <p className="text-[11.5px] font-semibold" style={{ color: C.indigoText }}>{title}</p>
            {items.map(([k, lbl]) => (
              <div key={k} className="mt-1 grid grid-cols-[1fr_52px_52px] items-center gap-x-2">
                <span className="text-[12.5px] font-medium" style={{ color: C.ink }}>{lbl}</span>
                {["prev", "year"].map((fld) => (
                  <input key={fld} data-testid={`rec-${k}-${fld}`} type="number" min="0" inputMode="numeric" disabled={!f.can_edit}
                    value={(rec[k] || {})[fld] ?? 0} onChange={(e) => setRec(k, fld, e.target.value)}
                    className="w-full rounded-lg border px-1.5 py-1 text-center text-[13px] font-bold outline-none" style={{ borderColor: C.line, color: C.ink }} />
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
      {f.can_edit && (
        <button data-testid="fg-save" onClick={save} disabled={saving}
          className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full py-2.5 text-[14px] font-semibold text-white disabled:opacity-60" style={{ background: C.indigo }}>
          <CheckCircle size={16} weight="bold" /> {t("save")}
        </button>
      )}
    </Card>
  );
}


function GlobalGrantsCard({ clubId }) {
  const { t } = useT();
  const [g, setG] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get(`/clubs/${clubId}/global-grants`).then((r) => setG(r.data)).catch(() => {});
  useEffect(() => { load(); }, [clubId]);
  if (!g) return (
    <Card data-testid="global-grants-card" style={{ border: "1.5px solid #0F8A5F" }}>
      <p className="text-[16px] font-bold" style={{ color: C.ink }}>{t("club_global_grants")}</p>
      <div className="flex items-center justify-center py-10"><CircleNotch size={22} className="animate-spin" style={{ color: C.sub }} /></div>
    </Card>
  );
  const setRow = (lk, i, k, v) => { const l = [...g[lk]]; l[i] = { ...l[i], [k]: v }; setG({ ...g, [lk]: l }); };
  const addRow = (lk) => setG({ ...g, [lk]: [...g[lk], { name: "", club_share: 0, foundation_share: 0, partner_share: 0 }] });
  const delRow = (lk, i) => { const l = [...g[lk]]; l.splice(i, 1); setG({ ...g, [lk]: l }); };
  const save = async () => {
    setSaving(true);
    try { await api.put(`/clubs/${clubId}/global-grants`, { projects_last_5y: g.projects_last_5y, ongoing: g.ongoing, this_year: g.this_year }); toast.success("Global grants saved"); await load(); }
    catch { toast.error("Could not save"); }
    setSaving(false);
  };
  const rowTotal = (r) => (parseFloat(r.club_share) || 0) + (parseFloat(r.foundation_share) || 0) + (parseFloat(r.partner_share) || 0);
  const listTotal = (lk) => g[lk].reduce((s, r) => s + rowTotal(r), 0);
  const usd = (n) => "$" + (Number(n) || 0).toLocaleString();
  const grand = listTotal("ongoing") + listTotal("this_year");
  const COLS = "sm:grid-cols-[1fr_120px_120px_120px_110px_34px]";
  const Section = ({ lk, label }) => (
    <div className="mt-4">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-[14px] font-bold" style={{ color: C.ink }}>{label} <span className="font-medium" style={{ color: C.sub }}>({g[lk].length}) · {usd(listTotal(lk))}</span></p>
        {g.can_edit && <button data-testid={`gg-add-${lk}`} onClick={() => addRow(lk)} className="inline-flex items-center gap-1 text-[13px] font-semibold" style={{ color: C.indigoText }}><Plus size={13} weight="bold" /> Add project</button>}
      </div>
      <div className={`mb-1 hidden gap-2 px-2 text-[11px] font-semibold uppercase tracking-wide sm:grid ${COLS}`} style={{ color: C.sub }}>
        <span>Project</span><span>Club share</span><span>Foundation</span><span>Partner</span><span>Total</span><span></span>
      </div>
      {g[lk].map((r, i) => (
        <div key={i} className={`mb-2 grid grid-cols-1 items-center gap-2 rounded-xl border p-2 ${COLS}`} style={{ borderColor: C.line }} data-testid={`gg-${lk}-${i}`}>
          <input data-testid={`gg-${lk}-${i}-name`} value={r.name || ""} disabled={!g.can_edit} onChange={(e) => setRow(lk, i, "name", e.target.value)} placeholder="Project name" className="rounded-lg border px-2.5 py-1.5 text-[14px] font-semibold outline-none" style={{ borderColor: C.line, color: C.ink }} />
          {["club_share", "foundation_share", "partner_share"].map((k) => (
            <div key={k} className="flex items-center gap-1 rounded-lg border px-2 py-1.5" style={{ borderColor: C.line }}>
              <span style={{ color: C.sub }}>$</span>
              <input data-testid={`gg-${lk}-${i}-${k}`} type="number" min="0" inputMode="decimal" disabled={!g.can_edit} value={r[k] ?? 0} onChange={(e) => setRow(lk, i, k, e.target.value)} className="w-full bg-transparent text-[14px] font-bold outline-none" style={{ color: C.ink }} />
            </div>
          ))}
          <span className="px-1 text-[14px] font-bold" style={{ color: C.indigoText }}>{usd(rowTotal(r))}</span>
          {g.can_edit ? <button data-testid={`gg-del-${lk}-${i}`} onClick={() => delRow(lk, i)} className="justify-self-start sm:justify-self-center" style={{ color: C.cranberry }}><Trash size={15} weight="bold" /></button> : <span />}
        </div>
      ))}
      {g[lk].length === 0 && <p className="px-2 text-[13px]" style={{ color: C.sub }}>None yet.</p>}
    </div>
  );
  return (
    <Card data-testid="global-grants-card" style={{ border: "1.5px solid #0F8A5F" }}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><p className="text-[16px] font-bold" style={{ color: C.ink }}>{t("club_global_grants")}</p><p className="text-[12.5px]" style={{ color: C.sub }}>{t("club_grants_sub")}</p></div>
        <label className="flex items-center gap-2 text-[13px] font-medium" style={{ color: C.sub }}>Projects in last 5 years
          <input data-testid="gg-last5" type="number" min="0" inputMode="numeric" disabled={!g.can_edit} value={g.projects_last_5y ?? 0} onChange={(e) => setG({ ...g, projects_last_5y: e.target.value })} className="w-20 rounded-lg border px-2.5 py-1.5 text-[15px] font-bold outline-none" style={{ borderColor: C.line, color: C.ink }} />
        </label>
      </div>
      <Section lk="ongoing" label={t("club_ongoing")} />
      <Section lk="this_year" label={t("club_initiated_this_year")} />
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl px-4 py-3" style={{ background: C.indigoSoft }}>
        <span className="text-[13px] font-semibold uppercase tracking-wide" style={{ color: C.sub }}>{t("club_total_grant_funding")}</span>
        <span className="text-[20px] font-bold" data-testid="gg-grand" style={{ color: C.indigoText }}>{usd(grand)}</span>
      </div>
      {g.can_edit && (
        <button data-testid="gg-save" onClick={save} disabled={saving} className="mt-3 inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-[14px] font-semibold text-white disabled:opacity-60" style={{ background: C.indigo }}>
          <CheckCircle size={16} weight="bold" /> {t("save")}
        </button>
      )}
    </Card>
  );
}


function InfoDialog({ clubId, item, initial, canEdit, open, onClose, reload }) {
  const { t } = useT();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setText(initial || ""); }, [initial, item]);
  if (!item) return null;
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/clubs/${clubId}/checklist/${item.item_id}/info`, { text });
      toast.success("Saved");
      await reload();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="info-dialog">
        <DialogHeader><DialogTitle>{item.name}</DialogTitle></DialogHeader>
        {canEdit ? (
          <textarea data-testid="info-dialog-text" value={text} onChange={(e) => setText(e.target.value)} rows={4}
            placeholder="Type the details here…"
            className="w-full rounded-xl border px-3 py-2.5 text-[15px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line }} />
        ) : (
          <p className="whitespace-pre-wrap rounded-lg p-3 text-[15px]" style={{ background: "#F5F6FB", color: C.ink }} data-testid="info-dialog-readonly">{text || "—"}</p>
        )}
        {canEdit && (
          <DialogFooter>
            <button data-testid="info-dialog-save" onClick={save} disabled={busy}
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[15px] font-bold text-white disabled:opacity-60" style={{ background: C.indigo }}>
              {busy ? <CircleNotch size={15} className="animate-spin" /> : <CheckCircle size={15} weight="bold" />} {t("save")}
            </button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}


function MembershipGraphsCard({ clubId, con }) {
  const { t } = useT();
  const [g, setG] = useState(null);
  useEffect(() => { api.get(`/clubs/${clubId}/gender-mix`).then((r) => setG(r.data)).catch(() => {}); }, [clubId]);
  const GC = { male: "#4C7DF0", female: "#E5679A", other: "#14B8A6" };
  const GLBL = { male: t("club_male"), female: t("club_female"), other: t("club_other") };
  const sum = (o) => (o ? (o.male || 0) + (o.female || 0) + (o.other || 0) : 0);
  const memberCount = con?.member_count ?? 0;
  const registered = con?.registered ?? 0;
  const regPct = memberCount ? Math.round((registered / memberCount) * 100) : 0;

  const Donut = ({ data, label }) => {
    const tot = sum(data);
    const R = 46, CIRC = 2 * Math.PI * R;
    let acc = 0;
    return (
      <div className="flex flex-col items-center">
        <svg width="132" height="132" viewBox="0 0 132 132">
          <circle cx="66" cy="66" r={R} fill="none" stroke="#EEF1F6" strokeWidth="15" />
          {tot > 0 && ["male", "female", "other"].map((k) => {
            const frac = (data[k] || 0) / tot; if (!frac) return null;
            const seg = (
              <circle key={k} cx="66" cy="66" r={R} fill="none" stroke={GC[k]} strokeWidth="15"
                strokeDasharray={`${frac * CIRC} ${CIRC - frac * CIRC}`} strokeDashoffset={-acc * CIRC}
                transform="rotate(-90 66 66)" />
            );
            acc += frac; return seg;
          })}
          <text x="66" y="62" textAnchor="middle" style={{ fontSize: 26, fontWeight: 800, fill: C.ink }}>{tot}</text>
          <text x="66" y="82" textAnchor="middle" style={{ fontSize: 11, fill: C.sub, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</text>
        </svg>
        <div className="mt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
          {["male", "female", "other"].map((k) => (
            <span key={k} className="inline-flex items-center gap-1.5 text-[12px] font-semibold" style={{ color: C.ink }}>
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: GC[k] }} /> {GLBL[k]} {(data?.[k] || 0)}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const StackedBar = ({ data, label }) => {
    const tot = sum(data);
    return (
      <div>
        <div className="mb-1.5 flex items-baseline justify-between">
          <span className="text-[13px] font-semibold" style={{ color: C.ink }}>{label}</span>
          <span className="text-[13px] font-bold" style={{ color: C.ink }}>{tot}</span>
        </div>
        <div className="flex h-4 w-full overflow-hidden rounded-full" style={{ background: "#EEF1F6" }}>
          {["male", "female", "other"].map((k) => {
            const w = tot ? ((data[k] || 0) / tot) * 100 : 0;
            return w ? <div key={k} style={{ width: `${w}%`, background: GC[k] }} title={`${GLBL[k]}: ${data[k]}`} /> : null;
          })}
        </div>
        <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
          {["male", "female", "other"].map((k) => (
            <span key={k} className="inline-flex items-center gap-1 text-[11.5px] font-medium" style={{ color: C.sub }}>
              <span className="h-2 w-2 rounded-full" style={{ background: GC[k] }} /> {GLBL[k]} {(data?.[k] || 0)}
            </span>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Card data-testid="membership-graphs-card" style={{ border: "1.5px solid #0EA5A0" }}>
      <div className="mb-4 flex items-center gap-2">
        <ChartPieSlice size={18} weight="fill" style={{ color: C.indigo }} />
        <h2 className="text-[18px] font-bold tracking-tight" style={{ color: C.ink }}>Membership</h2>
      </div>
      {!g ? (
        <div className="flex items-center justify-center py-10"><CircleNotch size={22} className="animate-spin" style={{ color: C.sub }} /></div>
      ) : (
        <div className="grid grid-cols-1 items-center gap-x-6 gap-y-5 sm:grid-cols-2">
          <Donut data={g.members} label="Total members" />
          <StackedBar data={g.board} label="Board of directors" />
          <StackedBar data={g.inducted} label="New members inducted" />
          <div>
            <div className="mb-1.5 flex items-baseline justify-between">
              <span className="text-[13px] font-semibold" style={{ color: C.ink }}>Registered on My Rotary</span>
              <span className="text-[13px] font-bold" style={{ color: C.ink }}>{registered}/{memberCount}</span>
            </div>
            <div className="h-4 w-full overflow-hidden rounded-full" style={{ background: "#EEF1F6" }}>
              <div className="h-full rounded-full transition-all" style={{ width: `${regPct}%`, background: C.indigo }} />
            </div>
            <p className="mt-1.5 text-[12px] font-medium" style={{ color: C.sub }}>{regPct}% of members registered</p>
          </div>
        </div>
      )}
    </Card>
  );
}


export default function ClubDashboard({ clubId: clubIdProp, clubName, clubRole, dgView = false }) {
  const params = useParams();
  const clubId = clubIdProp || params.clubId;
  const { t } = useT();
  const [dash, setDash] = useState(null);
  const [con, setCon] = useState(null);
  const [prep, setPrep] = useState(null);
  const [sponsored, setSponsored] = useState(null);
  const [meetingsKind, setMeetingsKind] = useState(null);
  const [infoItem, setInfoItem] = useState(null);
  const navigate = useNavigate();

  const loadPrep = () => api.get(`/clubs/${clubId}/prep`).then((r) => setPrep(r.data)).catch(() => {});
  const loadDash = () => api.get(`/dashboard/club/${clubId}`).then((r) => setDash(r.data)).catch(() => setDash({ error: true }));
  useEffect(() => {
    if (!clubId) return;
    loadDash();
    api.get(`/clubs/${clubId}/constitution`).then((r) => setCon(r.data)).catch(() => setCon({}));
    loadPrep();
  }, [clubId]); // eslint-disable-line

  if (!dash || !con || !prep) return <Loader label={t("club_loading")} />;
  if (dash.error) return <p className="text-[16px]" style={{ color: C.sub }}>{t("club_load_error")}</p>;

  const v = dash.visit;
  if (dgView) {
    const r = dash.readiness;
    const boardLeft = Math.max(r.board_required - r.board_filled, 0);
    return (
      <div data-testid="club-dashboard" className="space-y-2">
        {v ? (
          <span data-testid="club-visit-status" className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[14px] font-semibold" style={{ background: C.indigoSoft, color: C.indigoText }}>
            {t("club_govs_visit")} · {VISIT_WORD[v.status] || v.status} · {fmtDate(v.scheduled_start_utc)}
          </span>
        ) : <p className="text-[15px]" data-testid="club-no-visit" style={{ color: C.sub }}>{t("club_no_visit")}</p>}
        <div className="flex items-center gap-2.5 py-1 text-[15px] font-medium" style={{ color: C.ink }}>
          {boardLeft ? <WarningCircle size={18} weight="fill" style={{ color: "#B5730A" }} /> : <CheckCircle size={18} weight="fill" style={{ color: "#0F8A5F" }} />}
          {boardLeft ? `${boardLeft} board position(s) still empty` : t("club_all_board_filled")}
        </div>
      </div>
    );
  }

  const docItems = (prep.items || []).filter((it) => !isRoster(it.name));
  const docsFor = (id) => prep.documents.filter((d) => d.item_id === id);
  const infoFor = (id) => (prep.info_answers?.[id]?.text || "").trim().length > 0;
  const done = docItems.filter((it) => docsFor(it.item_id).length > 0 || infoFor(it.item_id)).length;
  const pct = docItems.length ? Math.round((done / docItems.length) * 100) : 100;

  const submit = async () => {
    try { const r = await api.post(`/clubs/${clubId}/prep/submit`); toast.success(r.data.incomplete ? "Submitted (some items outstanding)" : "Submitted to district"); await loadPrep(); }
    catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
  };
  const save = async () => { await loadPrep(); toast.success(t("save")); };
  const boardStatus = con.board?.every((b) => b.status === "submitted") ? "submitted" : null;
  const commStatus = [...(con.committee || []), ...(con.subcommittee || [])].length && [...(con.committee || []), ...(con.subcommittee || [])].every((b) => b.status === "submitted") ? "submitted" : null;

  return (
    <div className="mx-auto w-full max-w-[1180px] space-y-6" data-testid="club-dashboard">
      <nav className="flex flex-wrap items-center gap-1.5 text-[13px] font-medium" data-testid="club-breadcrumb">
        {dash.can_view_district ? (
          <button data-testid="crumb-district" onClick={() => navigate("/")} style={{ color: C.indigo }}>{dash.district_name || `District ${dash.district_no || ""}`}</button>
        ) : <span style={{ color: C.sub }}>{dash.district_name || `District ${dash.district_no || ""}`}</span>}
        {dash.zone_name && (
          <>
            <span style={{ color: C.sub }}>/</span>
            {dash.can_view_district ? (
              <button data-testid="crumb-zone" onClick={() => navigate(`/?zone=${dash.zone_id}`)} style={{ color: C.indigo }}>{dash.zone_name}</button>
            ) : <span style={{ color: C.sub }}>{dash.zone_name}</span>}
          </>
        )}
        <span style={{ color: C.sub }}>/</span>
        <span data-testid="crumb-club" style={{ color: C.sub }}>{clubName || dash.club_name}</span>
      </nav>
      <PageHero title={clubName || dash.club_name}
        right={<HeroVisit clubId={clubId} visit={v} mock={prep.mock_gov} reload={loadPrep} canParticipate={prep.mock_can_participate} />} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        {/* Left + centre block — Global Grants sits under it, spanning left rail + centre */}
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[300px_minmax(0,1fr)]">
            {/* Left rail: roles */}
            <div className="space-y-4">
          {prep.action_points?.length > 0 && (
            <Card data-testid="club-action-points" style={{ border: "1.5px solid #E5484D" }}>
              <div className="mb-2 flex items-center gap-2">
                <ClipboardText size={18} weight="fill" style={{ color: C.indigo }} />
                <h2 className="text-[15px] font-bold tracking-tight" style={{ color: C.ink }}>{t("club_action_points_title")}</h2>
              </div>
              <div className="space-y-2">
                {prep.action_points.map((p, i) => (
                  <div key={i} className="rounded-xl border p-2.5" style={{ borderColor: C.line, background: "#FBFBFE" }} data-testid={`club-ap-${i}`}>
                    <p className="text-[14px] font-semibold" style={{ color: C.ink }}>{p.text}</p>
                    {(p.assignee || p.instructions) && (
                      <p className="mt-0.5 text-[12.5px]" style={{ color: C.sub }}>{p.assignee ? `For ${p.assignee}` : ""}{p.instructions ? `${p.assignee ? " · " : ""}${p.instructions}` : ""}</p>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}
          <RoleCard icon={IdentificationBadge} tint={C.indigo} testid="card-board" status={boardStatus}
            title={t("club_board_title")} sub={con.board_complete ? t("club_board_ready") : t("club_board_assign")} onClick={() => navigate(`/board/${clubId}`)} />
          <RoleCard icon={UsersThree} tint={C.turquoise} testid="card-committees" status={commStatus}
            title={t("club_committees_title")} sub={con.committees_complete ? t("club_committees_done") : t("club_committees_assign")} onClick={() => navigate(`/committees/${clubId}`)} />
          <RoleCard icon={AddressBook} tint={C.gold} testid="card-directory"
            title={t("club_directory_title")} sub={con.directory_ready ? t("club_directory_ready") : t("club_directory_locked")}
            onClick={() => navigate(`/directory/${clubId}`)} />
          <button data-testid="manage-members" onClick={() => navigate(`/members/${clubId}`)} className="inline-flex w-full items-center justify-center gap-2 rounded-full py-2.5 text-[14px] font-semibold" style={{ color: C.indigoText, background: C.indigoSoft }}>
            <UserPlus size={16} weight="bold" /> {t("club_manage_members")} ({con.member_count})
          </button>
          <div className="rounded-xl px-3 py-2.5 text-center" style={{ background: "#EEF2FF" }} data-testid="club-registration">
            <span className="text-[13px] font-semibold" style={{ color: C.indigoText }}>{t("club_registered")}: {con.registered ?? 0}/{con.member_count ?? 0}</span>
          </div>
          <GenderMixCard clubId={clubId} />
            </div>

            {/* Centre: documents + membership */}
            <div className="space-y-4">
              <Card data-testid="documents-card" style={{ border: "1.5px solid #7C4DFF" }}>
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-[18px] font-bold tracking-tight" style={{ color: C.ink }}>{t("club_documents")}</h2>
                  <span className="text-[14px] font-semibold" style={{ color: C.sub }}>{done}/{docItems.length} · {pct}%</span>
                </div>
                <div className="mb-4 h-2 w-full overflow-hidden rounded-full" style={{ background: "#EEF1F6" }}>
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: C.indigo }} data-testid="doc-progress" />
                </div>
                {docItems.length === 0 ? (
                  <p className="py-4 text-[15px]" style={{ color: C.sub }}>{t("club_no_doc_list")}</p>
                ) : (
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    {DOC_GROUPS.map((g) => {
                      const items = docItems.filter((it) => docGroup(it.name) === g);
                      if (items.length === 0) return null;
                      return (
                        <DocFolder key={g} title={g} items={items} isDone={(it) => docsFor(it.item_id).length > 0 || infoFor(it.item_id)}
                          shared={(it) => ({ clubId, docs: docsFor(it.item_id), hasInfo: infoFor(it.item_id), canEdit: prep.can_edit, reload: loadPrep, navigate, onSponsored: setSponsored, onMeetings: setMeetingsKind, onInfo: setInfoItem })} />
                      );
                    })}
                  </div>
                )}
                {prep.can_edit && prep.prep_status !== "submitted" && (
                  <button data-testid="save-prep" onClick={save} className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full py-3 text-[16px] font-semibold" style={{ color: C.indigoText, background: C.indigoSoft }}>
                    <CheckCircle size={16} weight="bold" /> {t("save")}
                  </button>
                )}
              </Card>
              {/* Membership graphs sit directly under the documents box, within the centre grid */}
              <MembershipGraphsCard clubId={clubId} con={con} />
            </div>
          </div>

          {/* Global Grants sits under membership, spanning left rail + centre */}
          <GlobalGrantsCard clubId={clubId} />
        </div>

        {/* Right rail: foundation giving */}
        <div className="space-y-4 self-start">
          <FoundationGivingCard clubId={clubId} />
          <GivingBandsCard endpoint={`/foundation-giving/clubs/${clubId}/bands`} subtitle="This club · by donor band" />
        </div>
      </div>

      {/* Single submission after all data is entered */}
      {prep.can_edit && prep.prep_status !== "submitted" && (
        <button data-testid="submit-prep" onClick={submit} className="inline-flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-[16px] font-semibold text-white" style={{ background: C.indigo }}>
          <PaperPlaneTilt size={18} weight="bold" /> {t("club_submit")}
        </button>
      )}
      {prep.prep_status === "submitted" && (
        <p className="text-center text-[15px] font-semibold" style={{ color: "#0F8A5F" }} data-testid="prep-submitted">{prep.submitted_incomplete ? t("club_submitted_incomplete") : t("club_submitted")}</p>
      )}
      <SponsoredClubsDialog clubId={clubId} item={sponsored} open={!!sponsored} onClose={() => setSponsored(null)} reload={loadPrep} />
      <MeetingsDialog clubId={clubId} kind={meetingsKind} open={!!meetingsKind} onClose={() => setMeetingsKind(null)} reload={loadPrep} />
      <InfoDialog clubId={clubId} item={infoItem} initial={infoItem ? (prep.info_answers?.[infoItem.item_id]?.text || "") : ""}
        canEdit={prep.can_edit} open={!!infoItem} onClose={() => setInfoItem(null)} reload={loadPrep} />
    </div>
  );
}
